import * as Sentry from '@sentry/react';
import type { S3UploadFields } from './upload-to-s3';

/**
 * Uploads a file to S3 with progress tracking using XMLHttpRequest
 * @param id - Asset identifier
 * @param url - S3 upload URL
 * @param file - File to upload
 * @param uploadFields - S3 upload fields (credentials, policy, etc.)
 * @param onProgress - Callback function called with progress percentage (0-100)
 * @param onSuccess - Callback function called on successful upload
 * @param onError - Callback function called on error
 */
const uploadToS3WithProgress = async (
  id: string,
  url: string,
  file: File,
  uploadFields: S3UploadFields,
  onProgress: (progress: number) => void,
  onSuccess: () => Promise<void>,
  onError: (error?: Error) => void,
): Promise<void> => {
  const formData = new FormData();

  for (const [key, value] of Object.entries(uploadFields)) {
    formData.append(key, value);
  }

  formData.append('file', file);

  Sentry.addBreadcrumb({
    message: 'Uploading file to S3 with progress tracking.',
    level: 'info',
    data: { url, fileSize: file.size, fileType: file.type, asset_id: id },
  });

  return new Promise((resolve, reject) => {
    // Using XHR instead of fetch because it provides native upload progress events
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', async () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        Sentry.addBreadcrumb({
          message: 'S3 upload with progress tracking successful.',
          level: 'info',
          data: { asset_id: id },
        });

        await onSuccess();
        resolve();
      } else {
        Sentry.addBreadcrumb({
          message: `S3 upload request was not successful - ${xhr.status} ${xhr.statusText}`,
          level: 'error',
          data: { status: xhr.status, statusText: xhr.statusText, asset_id: id },
        });

        const error = new Error(`${id} S3 upload request was not successful - ${xhr.status} ${xhr.statusText}`);
        onError(error);
        reject(error);
      }
    });

    xhr.addEventListener('error', () => {
      Sentry.addBreadcrumb({
        message: 'S3 upload network error.',
        level: 'error',
        data: { asset_id: id },
      });

      const error = new Error(`${id} S3 upload network error`);
      onError(error);
      reject(error);
    });

    xhr.open('POST', url);
    xhr.send(formData);
  });
};

export { uploadToS3WithProgress };
